// /opt/ptg01-v1/front-extranet/components/QuickActionsFab.tsx
// Bouton flottant « + » global (toutes pages dashboard) ouvrant une sheet de raccourcis
// vers les actions les plus fréquentes du consultant :
//   - Scanner un ticket (NDF-S5b, 2026-09-29 : en premier, D-S5-2)
//   - Valider mon CRA
//   - Créer une note de frais (default = ponctuelle)
//   - Créer un abonnement (note de frais récurrente)
//
// Mobile : FAB rond bottom-right au-dessus du BottomTabBar (avec safe-area iOS).
// Desktop : FAB rond bottom-right.
// Sheet : bottom sheet mobile / modal centré desktop.
'use client';

import React, { useEffect, useState } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { Plus, X, CalendarCheck, Receipt, Repeat, ArrowRight, Camera } from 'lucide-react';

const ACTIONS = [
    {
        key: 'scan',
        label: 'Scanner un ticket',
        description: 'Une photo suffit : Nexvia lit le montant pour vous',
        href: '/depenses/scanner',
        icon: Camera,
    },
    {
        key: 'cra',
        label: 'Valider mon CRA',
        description: 'Vérifier et certifier votre déclaration mensuelle',
        href: '/cras',
        icon: CalendarCheck,
    },
    {
        key: 'ndf',
        label: 'Créer une note de frais',
        description: 'Saisir une note avec son montant',
        href: '/depenses/creer',
        icon: Receipt,
    },
    {
        key: 'abo',
        label: 'Créer un abonnement',
        description: 'Note de frais récurrente (internet, coworking, transport…)',
        href: '/depenses/abonnements/creer',
        icon: Repeat,
    },
];

export default function QuickActionsFab() {
    const router = useRouter();
    const pathname = usePathname() || '';
    const [open, setOpen] = useState(false);

    // Cache le FAB sur les pages d'auth (pré-login) — sécurité visuelle
    const isAuthPage = pathname.startsWith('/auth') || pathname.startsWith('/register');
    // NDF-S5b : la page « Scanner un ticket » EST l'action rapide ; sur téléphone, le bouton rond y masquait
    // le verdict de la photo et les boutons d'envoi.
    const isScanPage = pathname.startsWith('/depenses/scanner');

    // Lock scroll body quand sheet ouverte
    useEffect(() => {
        if (!open) return;
        const original = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = original; };
    }, [open]);

    // Esc pour fermer
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open]);

    const go = (href: string) => {
        setOpen(false);
        router.push(href);
    };

    if (isAuthPage || isScanPage) return null;

    return (
        <>
            {/* FAB — toujours visible sauf sur auth */}
            <button
                type="button"
                onClick={() => setOpen(true)}
                aria-label="Actions rapides"
                title="Actions rapides"
                data-testid="quick-actions-fab"
                className="fixed right-4 lg:right-6 z-40 w-14 h-14 rounded-full bg-[#000091] text-white shadow-[0_8px_24px_rgba(0,0,145,0.35)] hover:bg-[#1e40af] active:scale-95 transition-all flex items-center justify-center"
                style={{ bottom: 'calc(80px + env(safe-area-inset-bottom))' }}
            >
                <Plus className="w-6 h-6" strokeWidth={2.5} />
            </button>

            {/* Sheet / modal */}
            {open && (
                <>
                    <button
                        aria-label="Fermer"
                        onClick={() => setOpen(false)}
                        className="fixed inset-0 z-[55] bg-slate-900/40 backdrop-blur-sm"
                    />
                    <div
                        role="dialog"
                        aria-modal="true"
                        aria-label="Actions rapides"
                        className="fixed z-[56] bg-white shadow-2xl flex flex-col
                                   inset-x-0 bottom-0 rounded-t-2xl border-t-2 border-[#000091]/20 max-h-[85vh]
                                   sm:inset-auto sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:w-[440px] sm:max-w-[calc(100vw-2rem)] sm:rounded-2xl sm:border-2 sm:border-[#000091]/25 sm:max-h-[80vh]"
                        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
                    >
                        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 shrink-0">
                            <div>
                                <h2 className="text-[15px] font-black text-slate-900 leading-tight">Actions rapides</h2>
                                <p className="text-[11px] text-slate-500">Que voulez-vous faire ?</p>
                            </div>
                            <button
                                onClick={() => setOpen(false)}
                                className="p-1.5 rounded-lg hover:bg-slate-100"
                                aria-label="Fermer"
                            >
                                <X className="w-4 h-4 text-slate-500" />
                            </button>
                        </div>
                        <div className="overflow-y-auto py-1">
                            {ACTIONS.map(a => {
                                const Icon = a.icon;
                                return (
                                    <button
                                        key={a.key}
                                        type="button"
                                        data-testid={`quick-action-${a.key}`}
                                        onClick={() => go(a.href)}
                                        className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-[#000091]/[0.04] active:bg-[#000091]/[0.08] transition-colors group"
                                    >
                                        <div className="w-10 h-10 rounded-xl bg-[#000091]/10 text-[#000091] flex items-center justify-center shrink-0">
                                            <Icon className="w-5 h-5" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-[13px] font-bold text-slate-900">{a.label}</div>
                                            <div className="text-[11px] text-slate-500 leading-snug truncate">{a.description}</div>
                                        </div>
                                        <ArrowRight className="w-4 h-4 text-[#000091] shrink-0 group-hover:translate-x-0.5 transition-transform" strokeWidth={2.5} />
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </>
            )}
        </>
    );
}
